import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import type { VhIconName } from './vh-icons';

export type VhIconSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_MAP: Record<VhIconSize, number> = {
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

/**
 * Thin wrapper around `<lucide-angular>` that resolves icons from the
 * VendorHub registry and picks a sensible stroke width per size. Keep
 * this dumb; all semantics (aria-label, colour) come from the caller.
 */
@Component({
  selector: 'vh-icon',
  standalone: true,
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<lucide-angular
    [name]="name()"
    [size]="sizePx()"
    [strokeWidth]="strokeWidth()"
    [attr.aria-hidden]="ariaLabel() ? null : 'true'"
    [attr.aria-label]="ariaLabel()"
    [attr.role]="ariaLabel() ? 'img' : null"
  />`,
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        color: currentColor;
      }
    `,
  ],
})
export class VhIconComponent {
  readonly name = input.required<VhIconName>();
  readonly size = input<VhIconSize>('md');
  readonly strokeWidth = input<number>(1.75);
  readonly ariaLabel = input<string | null>(null);

  readonly sizePx = computed(() => SIZE_MAP[this.size()]);
}
